import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiUser, FiClock } from 'react-icons/fi';
import { API_URL } from '../../api/client';

interface TicketMessage {
  message_id: string;
  message: string;
  is_staff_reply: boolean;
  created_at: string;
  first_name: string;
  last_name: string;
}

interface TicketDetails {
  ticket_id: string;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  messages: TicketMessage[];
}

const SupportTicketDetails: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/customer/support/tickets/${ticketId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setTicket(result.data);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/customer/support/tickets/${ticketId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: replyMessage }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setReplyMessage('');
        fetchTicketDetails();
      } else {
        alert(result.message || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('An error occurred while sending the reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center">
          <p className="text-gray-600">Ticket not found</p>
          <button
            onClick={() => navigate('/customer/support')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Back to Support
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/customer/support')}
          className="flex items-center gap-2 text-blue-600 hover:underline mb-4"
        >
          <FiArrowLeft /> Back to Support
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{ticket.subject}</h1>
            <p className="text-gray-600">Ticket #{ticket.ticket_number}</p>
          </div>
          <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(ticket.status)}`}>
            {ticket.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Ticket Info Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Category</p>
            <p className="font-medium">{ticket.category}</p>
          </div>
          <div>
            <p className="text-gray-600">Priority</p>
            <p className="font-medium capitalize">{ticket.priority}</p>
          </div>
          <div>
            <p className="text-gray-600">Created</p>
            <p className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Conversation</h2>
        <div className="space-y-4 max-h-[500px] overflow-y-auto">
          {ticket.messages.map((message) => (
            <div
              key={message.message_id}
              className={`flex gap-3 ${message.is_staff_reply ? 'flex-row' : 'flex-row-reverse'}`}
            >
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  message.is_staff_reply
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <FiUser />
              </div>
              <div className={`flex-1 max-w-2xl ${message.is_staff_reply ? 'text-left' : 'text-right'}`}>
                <div
                  className={`inline-block p-4 rounded-lg ${
                    message.is_staff_reply ? 'bg-blue-50 text-left' : 'bg-gray-50 text-left'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">
                      {message.first_name} {message.last_name}
                    </span>
                    {message.is_staff_reply && (
                      <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Support Team</span>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <FiClock size={12} />
                    {new Date(message.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reply Form */}
      {ticket.status !== 'closed' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Add Reply</h3>
          <form onSubmit={handleSendReply}>
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg resize-none"
              rows={4}
              placeholder="Type your message..."
            />
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={sending || !replyMessage.trim()}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <FiSend /> {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      )}

      {ticket.status === 'closed' && (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <p className="text-gray-600">This ticket has been closed. You can no longer add replies.</p>
        </div>
      )}
    </div>
  );
};

export default SupportTicketDetails;
